import React, { useContext } from "react";

import AppBar from "@mui/material/AppBar";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";

import Menu from "@mui/icons-material/Menu";

import {
  RefineThemedLayoutV2HeaderProps,
  useThemedLayoutContext,
} from "@refinedev/mui";

import { OrderSearchBar } from "./components/OrderSearchBar";
import { getRuntimeEnv } from "../../providers/getRuntimeEnv";
import { WorkspaceDrawer } from "../workspace/WorkspaceDrawer";
import { ColorModeContext } from "../../providers/colorProvider";
import { UserMenu } from "./UserMenu";
import { MarketplaceClock } from "./components/MarketplaceClock";
import { useTranslation } from "../../hooks/useTranslation";
import { SelectStore } from "../../pages/stores/select-stores/SelectStore";

// Replaces refine's built-in HamburgerMenu: its desktop toggle only ever
// collapses the sidebar (no way back), so this renders a symmetric
// collapse/expand icon on desktop instead, keeping the same mobile-drawer
// open button refine's version has.
const SidebarToggle = () => {
  const {
    siderCollapsed,
    setSiderCollapsed,
    mobileSiderOpen,
    setMobileSiderOpen,
  } = useThemedLayoutContext();
  const { translateWithoutPrefix } = useTranslation();

  return (
    <>
      <Tooltip
        title={translateWithoutPrefix(
          siderCollapsed ? "buttons.expandSidebar" : "buttons.collapseSidebar",
        )}
      >
        <IconButton
          color="inherit"
          edge="start"
          onClick={() => setSiderCollapsed(!siderCollapsed)}
          sx={{ mr: 2, display: { xs: "none", md: "flex" } }}
        >
          {siderCollapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Tooltip>
      <IconButton
        color="inherit"
        aria-label="open drawer"
        edge="start"
        onClick={() => setMobileSiderOpen(!mobileSiderOpen)}
        sx={{ mr: 2, display: { xs: "flex", md: "none" } }}
      >
        <Menu />
      </IconButton>
    </>
  );
};

type HeaderProps = RefineThemedLayoutV2HeaderProps & {
  showTitle?: boolean;
};

export const HeaderWithTitle: React.FC<HeaderProps> = (props: HeaderProps) => {
  return <Header {...props} showTitle />;
};

export const HeaderWithTitleAndMarketplace = HeaderWithTitle;

export const Header: React.FC<HeaderProps> = (props: HeaderProps) => {
  const { showTitle = false, sticky = false } = props;
  const runtimeEnv = getRuntimeEnv();
  const isDevEnv = runtimeEnv === "dev";
  const title = isDevEnv ? "SyncroDev" : "Syncrosale";
  const logo = isDevEnv ? "/icons/logo-dev.svg" : "/icons/logo.svg";
  const { themeProfile, toggleThemeProfile } = useContext(ColorModeContext);

  return (
    <>
      <WorkspaceDrawer />
      <AppBar position={sticky ? "sticky" : "relative"}>
        <Toolbar>
          {showTitle ? (
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              <img
                src={logo}
                alt="App Icon"
                style={{ width: 48, height: 48, objectFit: "contain" }}
              />
              <Typography
                variant="h6"
                component="div"
                fontWeight={700}
                sx={{ marginLeft: 1 }}
              >
                {title}
              </Typography>
            </Box>
          ) : (
            <SidebarToggle />
          )}

          {/* SEARCH BAR */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "center",
              mx: { xs: 1, md: 3 },
            }}
          >
            <OrderSearchBar />
          </Box>

          {/* RIGHT CONTROLS */}
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={3}
            sx={{ flexShrink: 0 }}
          >
            {isDevEnv && (
              <Tooltip
                title={`Tema önizleme: ${themeProfile === "dev" ? "Dev" : "Prod"} tema aktif`}
              >
                <Stack direction="row" alignItems="center" gap={0.5}>
                  <Typography variant="caption" fontWeight={600}>
                    {themeProfile === "dev" ? "DEV" : "PROD"}
                  </Typography>
                  <Switch
                    size="small"
                    checked={themeProfile === "prod"}
                    onChange={toggleThemeProfile}
                  />
                </Stack>
              </Tooltip>
            )}
            <SelectStore sx={{ minWidth: 140 }} />
            <MarketplaceClock />
            <Box sx={{ width: 220 }}>
              <UserMenu />
            </Box>
          </Stack>
        </Toolbar>
      </AppBar>
    </>
  );
};
